import { useState, useEffect, useCallback } from 'react';
import type { Subscription } from '@barter/shared';
import {
  getSubscriptionsFromDb,
  putSubscription,
  removeSubscription as removeSubFromDb,
} from '../db/database';

// Подписки чисто локальные: клиент сам сопоставляет новый снапшот
// с активными подписками и показывает in-app уведомление.
// Бот-рассылок больше нет (решение владельца).

const MAX_SUBSCRIPTIONS = 10;

interface UseSubscriptions {
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  toggle: (id: string, active: boolean) => Promise<void>;
  create: (categoryId: string, city?: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  refresh: () => void;
}

export function useSubscriptions(): UseSubscriptions {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const local = await getSubscriptionsFromDb();
        if (!cancelled) setSubscriptions(local);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const toggle = useCallback(async (id: string, active: boolean) => {
    setSubscriptions((prev) => prev.map((s) => (s.id === id ? { ...s, active } : s)));
    const existing = subscriptions.find((s) => s.id === id);
    if (existing) {
      await putSubscription({ ...existing, active });
    }
  }, [subscriptions]);

  const create = useCallback(
    async (categoryId: string, city?: string) => {
      const current = await getSubscriptionsFromDb();
      if (current.length >= MAX_SUBSCRIPTIONS) {
        throw new Error('Max subscriptions reached');
      }
      const sub: Subscription = {
        id: crypto.randomUUID(),
        categoryId,
        city,
        keywords: [],
        active: true,
        createdAt: Date.now(),
      };
      await putSubscription(sub);
      setSubscriptions((prev) => [...prev, sub]);
    },
    [],
  );

  const remove = useCallback(async (id: string) => {
    setSubscriptions((s) => s.filter((x) => x.id !== id));
    await removeSubFromDb(id);
  }, []);

  return { subscriptions, loading, error, toggle, create, remove, refresh };
}
